import * as React from 'react';
import { Target } from 'lucide-react';
import { Input } from '@/components/forms/Input';
import { Select } from '@/components/forms/Select';
import { PROSPECTION_STATUS_CONFIG } from '@/components/establishments/establishment-configs';
import type { EstablishmentFormMode, EstablishmentFormState } from '@/types/establishment.types';

/** {@link Select} option used by the editable prospection-status picker. */
interface SelectOption {
  value: string;
  label: string;
}

/** Props for {@link EstablishmentProspectionSection}. */
interface Props {
  mode: EstablishmentFormMode;
  form: EstablishmentFormState;
  onFieldChange: (field: string, value: unknown) => void;
  currentProspectionLabel?: string;
  editableProspectionOptions?: SelectOption[];
}

/** Prospection status and follow-up fields. */
export function EstablishmentProspectionSection({
  mode,
  form,
  onFieldChange,
  currentProspectionLabel,
  editableProspectionOptions = [],
}: Readonly<Props>) {
  function handleMeetingsChange(e: React.ChangeEvent<HTMLInputElement>): void {
    onFieldChange('prospectMeetingsCount', e.target.value);
  }
  function handleStatusChange(value: string): void {
    onFieldChange('prospectionStatus', value);
  }

  return (
    <div className="form-section">
      <h4 className="form-section-title">
        <Target className="w-4 h-4" />
        Prospection
      </h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {renderProspectionStatus({
          mode,
          form,
          currentProspectionLabel,
          editableProspectionOptions,
          onStatusChange: handleStatusChange,
        })}
        <Input
          label="Nombre de rendez-vous prospects"
          type="number"
          value={form.prospectMeetingsCount}
          onChange={handleMeetingsChange}
          placeholder="0"
          min={0}
          inputSize="sm"
        />
      </div>
    </div>
  );
}

/** Locked create-status field, or the editable status select on edit. */
function renderProspectionStatus(params: {
  mode: EstablishmentFormMode;
  form: EstablishmentFormState;
  currentProspectionLabel?: string;
  editableProspectionOptions: SelectOption[];
  onStatusChange: (value: string) => void;
}): React.ReactNode {
  if (params.mode === 'create') {
    return (
      <Input
        label="Statut de prospection"
        value={PROSPECTION_STATUS_CONFIG.to_define.label}
        readOnly
        disabled
        inputSize="sm"
      />
    );
  }

  const currentStatus = (
    <div>
      <span className="text-sm font-medium text-foreground">Statut de prospection (actuel)</span>
      <div className="mt-2">
        <span className="text-sm text-muted-foreground">{params.currentProspectionLabel}</span>
      </div>
    </div>
  );

  if (params.editableProspectionOptions.length > 0) {
    return (
      <>
        {currentStatus}
        <Select
          label="Statut de prospection"
          value={params.form.prospectionStatus}
          onChange={params.onStatusChange}
          options={params.editableProspectionOptions}
          placeholder="Sélectionner un statut"
          size="sm"
        />
      </>
    );
  }

  return (
    <>
      {currentStatus}
      <div>
        <span className="text-sm font-medium text-foreground">Statut de prospection</span>
        <p className="mt-2 text-sm text-muted-foreground">
          Ce statut est verrouillé et ne peut plus être modifié.
        </p>
      </div>
    </>
  );
}
